// ─── Semaines de repas : affecter un menu, l'appliquer au calendrier ──
// Porté de MoPCal (`services/repas.js`). C'est le chaînon qui manquait : un
// menu-type ne servait à rien tant qu'on ne pouvait pas le poser sur une
// semaine réelle et le voir dans le calendrier.
//
// Deux états distincts, et c'est volontaire :
//   • AFFECTÉ (`menuId`, applique=false) — la semaine sait quel menu elle
//     suivra, rien n'est encore écrit dans le calendrier ;
//   • APPLIQUÉ (applique=true) — les repas existent comme événements.
// On peut donc préparer ses semaines à l'avance sans polluer l'agenda.
import { collection, doc, deleteDoc, getDoc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '@mop/core';
import { cleCaseRepas, normaliserHeureRepas } from './menus';

const pad = (n) => String(n).padStart(2, '0');
const dk = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const _d = (s) => { const [a, m, j] = String(s).split('-').map(Number); return new Date(a, (m || 1) - 1, j || 1); };

// Lundi de la semaine contenant `date` (semaine ISO : lundi → dimanche).
export function lundiDe(date) {
  const d = new Date(date);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  d.setHours(0, 0, 0, 0);
  return d;
}
export const joursSemaine = (lundiDK) => Array.from({ length: 7 }, (_, i) => {
  const d = _d(lundiDK); d.setDate(d.getDate() + i); return dk(d);
});

// Les `n` prochaines semaines à partir de celle en cours.
export function prochainesSemaines(n = 8, depuis = new Date()) {
  const base = lundiDe(depuis);
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(base); d.setDate(base.getDate() + i * 7);
    return { lundi: dk(d) };
  });
}

// ── Persistance : repasSemaines/{calId}/semaines/{lundi} ────────────
const semainesRef = (calId) => collection(db, 'repasSemaines', calId, 'semaines');

export function ecouterSemaines(calId, callback, onErreur) {
  if (!calId) { callback([]); return () => {}; }
  return onSnapshot(semainesRef(calId),
    (snap) => callback(snap.docs.map(d => ({ lundi: d.id, ...d.data() }))),
    (e) => { callback([]); onErreur?.(e); });
}

async function ecrireSemaine(calId, lundiDK, menuId, applique) {
  await setDoc(doc(db, 'repasSemaines', calId, 'semaines', lundiDK),
    { menuId: menuId || null, applique: applique === true, maj: new Date().toISOString() }, { merge: true });
}

// Écrit les repas d'un jour SANS toucher aux autres événements : on ne remplace
// que ceux qui portent `source: 'repas'`.
async function setRepasJour(calId, dateKey, items) {
  const ref = doc(db, 'evenements', calId, 'jours', dateKey);
  const snap = await getDoc(ref);
  const existants = snap.exists() ? (snap.data().items || []) : [];
  const autres = existants.filter(e => e.source !== 'repas');
  await setDoc(ref, { items: [...autres, ...(items || [])] }, { merge: true });
}

// ── Recette en TEXTE dans la note de l'événement ────────────────────
// Pour l'avoir sous la main en cuisine sans rouvrir la fiche recette, et pour
// qu'elle suive dans les exports.
const ingsTexte = (ings) => (ings || [])
  .map(i => { const q = [i.quantite, i.unite].filter(Boolean).join(' '); return `• ${q ? q + ' ' : ''}${(i.nom || '').trim()}`.trimEnd(); })
  .filter(l => l !== '•').join('\n');

function recetteTexte(r) {
  if (!r) return '';
  const parts = [];
  const ing = ingsTexte(r.ingredients);
  if (ing) parts.push('Ingrédients :\n' + ing);
  if ((r.etapes || []).length) parts.push('Préparation :\n' + r.etapes.map((s, i) => `${i + 1}. ${(s || '').trim()}`).join('\n'));
  return parts.join('\n\n');
}

export function noteRepas(c, recettesById = {}, repasById = {}) {
  if (c?.recetteId && recettesById[c.recetteId]) {
    const r = recettesById[c.recetteId];
    const txt = recetteTexte(r);
    return txt ? `🍽️ ${r.nom || ''}\n\n${txt}` : '';
  }
  if (c?.repasId && repasById[c.repasId]) {
    const rp = repasById[c.repasId];
    const blocs = [];
    for (const x of (rp.recettes || [])) {
      const r = recettesById[x.recetteId];
      if (r) { const txt = recetteTexte(r); blocs.push(`— ${r.nom || ''} —${txt ? '\n' + txt : ''}`); }
    }
    const ex = ingsTexte(rp.extras);
    if (ex) blocs.push('Extras :\n' + ex);
    return blocs.length ? `🍽️ ${rp.nom || ''}\n\n${blocs.join('\n\n')}` : '';
  }
  return '';
}

// ── Actions ─────────────────────────────────────────────────────────
// Affecte SANS appliquer. Efface les repas déjà posés (menu précédent) pour ne
// pas laisser un mélange de deux menus sur la semaine.
export async function affecterMenu(calId, lundiDK, menuId) {
  await Promise.all(joursSemaine(lundiDK).map(d => setRepasJour(calId, d, [])));
  await ecrireSemaine(calId, lundiDK, menuId, false);
}

// Applique : crée un événement repas par case remplie du menu.
export async function appliquerMenu(calId, lundiDK, menu, moments, recettesById, repasById, auteur) {
  const jours = joursSemaine(lundiDK);
  const now = new Date().toISOString();
  for (let ji = 0; ji < 7; ji++) {
    const items = [];
    for (const mo of (moments || [])) {
      const c = (menu?.cases || {})[cleCaseRepas(ji, mo.id)];
      if (!c) continue;
      const nom = c.recetteId ? (recettesById[c.recetteId]?.nom || '')
        : c.repasId ? (repasById[c.repasId]?.nom || '')
        : (c.libre || '');
      if (!nom) continue;
      const hDeb = normaliserHeureRepas(mo.heureDebut || mo.heure);
      const hFin = normaliserHeureRepas(mo.heureFin);
      const note = noteRepas(c, recettesById, repasById);
      items.push({
        // id STABLE (menu + date + moment) : réappliquer ne crée pas de doublon.
        id: `repas_${menu.id}_${jours[ji]}_${mo.id}`,
        titre: `${mo.label} · ${nom}`,
        categorie: 'repas', source: 'repas',
        ...(hDeb ? { heure: hDeb } : {}),
        ...(hFin ? { heureFin: hFin } : {}),
        recetteId: c.recetteId || null, repasId: c.repasId || null,
        momentId: mo.id, menuId: menu.id,
        ...(note ? { note } : {}),
        creePar: auteur?.uid || null, creeParNom: auteur?.nom || '', creeLe: now,
      });
    }
    await setRepasJour(calId, jours[ji], items);
  }
  await ecrireSemaine(calId, lundiDK, menu.id, true);
}

// Retire du calendrier mais GARDE l'affectation : on peut réappliquer.
export async function desappliquerMenu(calId, lundiDK, menuId) {
  await Promise.all(joursSemaine(lundiDK).map(d => setRepasJour(calId, d, [])));
  await ecrireSemaine(calId, lundiDK, menuId, false);
}

// Détache complètement : repas effacés ET affectation supprimée. On attend tous
// les effacements avant de supprimer le doc, sinon une erreur laisserait des
// repas orphelins sans trace de la semaine.
export async function detacherSemaine(calId, lundiDK) {
  await Promise.all(joursSemaine(lundiDK).map(d => setRepasJour(calId, d, [])));
  await deleteDoc(doc(db, 'repasSemaines', calId, 'semaines', lundiDK));
}
